import { Box } from "@mui/material";
import Markdown from "markdown-to-jsx";

import { ChatLink } from "./ChatLink";

interface ChatMarkdownMessageBodyProps {
  /** The raw message text, rendered as markdown. */
  text: string;
  /** Drives the text and link color. */
  isIncoming: boolean;
}

/**
 * Renders a chat message body as markdown. Links (bare `http(s)://` URLs and
 * markdown links) render through {@link ChatLink}, keeping the platform-aware
 * open behavior and `data-dd-privacy` masking of the plain-text body.
 */
export function ChatMarkdownMessageBody(props: ChatMarkdownMessageBodyProps) {
  const { text, isIncoming } = props;

  return (
    <Box
      sx={(theme) => ({
        typography: "body2",
        color: isIncoming ? theme.palette.base?.strong : theme.palette.base?.inverted?.strong,
        // Collapse the default outer margins markdown adds around the first and
        // last block so the body sits flush inside the bubble.
        "& > :first-of-type": { marginTop: 0 },
        "& > :last-child": { marginBottom: 0 },
      })}
    >
      <Markdown
        options={{
          disableParsingRawHTML: true,
          overrides: {
            p: { component: Box, props: { component: "p", sx: { my: 1 } } },
            ul: { component: Box, props: { component: "ul", sx: { my: 1, pl: 5 } } },
            ol: { component: Box, props: { component: "ol", sx: { my: 1, pl: 5 } } },
            li: { component: Box, props: { component: "li", sx: { mb: 0.5 } } },
            strong: { component: Box, props: { component: "strong", sx: { fontWeight: 600 } } },
            a: { component: ChatLink, props: { isIncoming } },
          },
        }}
      >
        {text}
      </Markdown>
    </Box>
  );
}
